import { Flow, Modal, ModalBody, ModalFooter, ModalFooterButton, ModalHeader } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import { ACOMPANHAMENTO_TERRITORIO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { LoadingIndicator } from 'components/loading'
import { useProcessarUltimaFichaTerritorioMutation, useTerritorioMapaImovelQuery } from 'graphql/hooks.generated'
import { Redirect, useHistory, useRouteMatch } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { numeroTotalCidadaosImovel } from 'view/reterritorializacao/utils/reterritorializacao'
import { extractFamiliasResidentes } from 'view/visualizacao-imovel/utils-visualizacaoImovel'
import { TipoResponsavelEnum } from 'view/visualizacaoTerritorial/model'
import {
  getTipoImovelIcon,
  renderizarNumeroMoradores,
} from 'view/visualizacaoTerritorial/utils/visualizacaoTerritorialUtils'

import { PINO_TERRITORIO_MODAL_ROUTE } from '../model-territorioMapa'
import { PinoTerritorioModalBody } from './PinoTerritorioModalBody'
import { PinoTerritorioModalHeader } from './PinoTerritorioModalHeader'

interface PinoTerritorioModalProps {
  imovelId: string
}

export function PinoTerritorioModal(props: Readonly<PinoTerritorioModalProps>) {
  const { imovelId } = props

  const { url } = useRouteMatch()
  const history = useHistory()
  const handleRejection = useErrorHandler()

  const [processarTerritorio] = useProcessarUltimaFichaTerritorioMutation()

  const { data, loading, error } = useTerritorioMapaImovelQuery({
    variables: {
      id: imovelId,
    },
    fetchPolicy: 'network-only',
  })

  if (loading) {
    return (
      <Modal open>
        <ModalBody>
          <LoadingIndicator />
        </ModalBody>
      </Modal>
    )
  }

  if (error) {
    throw error
  }

  if (!data?.imovel) {
    return <Redirect to='/404' />
  }

  const tipoImovelIcon = getTipoImovelIcon(data?.imovel.tipoImovel, data?.imovel.condicoesMoradia?.tipoDomicilio)

  const familiasResidentes = extractFamiliasResidentes(data?.imovel?.familias)
  const numeroTotalCidadaos = numeroTotalCidadaosImovel(familiasResidentes)
  const isFichaNaoProcessadaAplicativo = isUndefinedOrNull(data?.imovel?.ultimaFichaCadastrada)

  const numeroMoradores =
    renderizarNumeroMoradores(
      isFichaNaoProcessadaAplicativo,
      tipoImovelIcon.tipoResponsavel === TipoResponsavelEnum.TECNICO,
      data.imovel.numeroMoradores ?? undefined,
      numeroTotalCidadaos
    ) ?? null

  const handleClose = () => {
    history.push(url.replace(`/${PINO_TERRITORIO_MODAL_ROUTE}/${imovelId}`, ''))
  }

  const handleVisualizarImovel = () => {
    processarTerritorio({ variables: { input: imovelId } }).catch(handleRejection)
    history.push(`${ACOMPANHAMENTO_TERRITORIO_PATH}/visualizarImovel/${imovelId}`)
  }

  return (
    <Modal open onClose={handleClose}>
      <ModalHeader>
        <PinoTerritorioModalHeader
          imovel={data?.imovel}
          numeroMoradores={numeroMoradores}
          dataUltimaVisita={data.imovel.dataUltimaVisita}
          tipoLocalizacao={data.imovel.condicoesMoradia.tipoLocalizacao ?? null}
          tipoDomicilio={data?.imovel.condicoesMoradia?.tipoDomicilio ?? null}
          tipoImovelIcon={tipoImovelIcon}
        />
      </ModalHeader>
      <ModalBody>
        <PinoTerritorioModalBody
          imovel={data?.imovel}
          dataUltimaAtualizacao={data?.imovel?.dataUltimaAtualizacao}
          familiasResidentes={familiasResidentes}
          isFichaProcessada={data.imovel.ultimaFichaCadastrada?.isProcessado ?? false}
        />
      </ModalBody>
      <ModalFooter>
        <Flow direction='horizontal' justifyContent='flex-end'>
          <ModalFooterButton onClick={handleClose}>Fechar</ModalFooterButton>
          <ModalFooterButton kind='primary' onClick={handleVisualizarImovel}>
            Visualizar imóvel
          </ModalFooterButton>
        </Flow>
      </ModalFooter>
    </Modal>
  )
}
